import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import ToastService from "../../Admin/services/ToastService";
import ClientService from "../services/ClientService";
import {
  SaPageShell,
  SaPageHeader,
  SaPrimaryButton,
  SaGhostButton,
  SaCard,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "./shared/SuperAdminUi";

function AddClient() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const toastService = useRef(new ToastService(toast));

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    agencyName: "",
    email: "",
    phoneNumber: "",
    streetaddress: "",
    city: "",
    state: "",
    country: "",
    postalcode: "",
    gstNumber: "",
    agencyLogo: null,
    license: "",
    registrationNumber: "",
    leadCodePrefix: "",
    isActive: true,
  });

  const [formError, setFormError] = useState({});

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const agencyLogo = reader.result.split(",")[1];
      setForm((prev) => ({ ...prev, agencyLogo }));
      setFormError((prev) => ({ ...prev, agencyLogo: "" }));
    };
    reader.onerror = () => {
      toast.current?.show({
        severity: "warn",
        summary: "Upload",
        detail: "Could not read the selected file.",
      });
    };
  };

  function validate() {
    const errors = {};
    if (!form.agencyName.trim()) errors.agencyName = "Agency name is required.";
    setFormError(errors);
    return Object.keys(errors).length === 0;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const requestData = {
      agencyName: form.agencyName,
      email: form.email,
      phoneNumber: form.phoneNumber,
      streetaddress: form.streetaddress,
      city: form.city,
      state: form.state,
      country: form.country,
      postalcode: form.postalcode,
      gstNumber: form.gstNumber,
      agencyLogo: form.agencyLogo,
      license: form.license,
      registrationNumber: form.registrationNumber,
      leadCodePrefix: form.leadCodePrefix,
      isActive: true,
    };
    try {
      await ClientService.createClient(requestData);
      toastService.current.showSuccess("Saved", "Client created successfully.");
      navigate("/client");
    } catch (error) {
      if (error.response?.status === 400) {
        toastService.current.showError("Validation", "Please check required fields.");
      } else {
        toastService.current.showError("Error", "Could not save client.");
      }
      console.error(error);
    } finally {
      setSaving(false);
    }
  }

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title="Add client"
        subtitle="Create a new agency workspace. You can add users and subscriptions after saving."
        actions={
          <>
            <SaGhostButton onClick={() => navigate("/client")}>
              <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
              Back to list
            </SaGhostButton>
          </>
        }
      />

      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-building">Agency details</SaSectionTitle>
          <div style={grid}>
            <SaField label="Agency name *">
              <input
                name="agencyName"
                value={form.agencyName}
                onChange={changeHandler}
                placeholder="e.g. Skyline Realty"
                style={{
                  ...saInputSx,
                  borderColor: formError.agencyName ? SA.danger : SA.border,
                }}
              />
              {formError.agencyName ? (
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError.agencyName}</p>
              ) : null}
            </SaField>
            <SaField label="Email">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={changeHandler}
                placeholder="contact@agency.com"
                style={saInputSx}
              />
            </SaField>
            <SaField label="Phone">
              <input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={changeHandler}
                placeholder="+91 …"
                style={saInputSx}
              />
            </SaField>
            <SaField label="Street address">
              <input
                name="streetaddress"
                value={form.streetaddress}
                onChange={changeHandler}
                placeholder="Street, area"
                style={saInputSx}
              />
            </SaField>
            <SaField label="City">
              <input name="city" value={form.city} onChange={changeHandler} placeholder="City" style={saInputSx} />
            </SaField>
            <SaField label="State">
              <input name="state" value={form.state} onChange={changeHandler} placeholder="State" style={saInputSx} />
            </SaField>
            <SaField label="Postal code">
              <input
                type="text"
                name="postalcode"
                value={form.postalcode}
                onChange={changeHandler}
                placeholder="Postal code"
                style={saInputSx}
              />
            </SaField>
            <SaField label="Country">
              <input name="country" value={form.country} onChange={changeHandler} placeholder="Country" style={saInputSx} />
            </SaField>
            <SaField label="GST number">
              <input name="gstNumber" value={form.gstNumber} onChange={changeHandler} placeholder="GSTIN" style={saInputSx} />
            </SaField>
            <SaField label="License">
              <input name="license" value={form.license} onChange={changeHandler} placeholder="License ID" style={saInputSx} />
            </SaField>
            <SaField label="Registration number">
              <input
                name="registrationNumber"
                value={form.registrationNumber}
                onChange={changeHandler}
                placeholder="Registration #"
                style={saInputSx}
              />
            </SaField>
            <SaField label="Lead code prefix" hint="Optional. Alphanumeric; new leads become PREFIX-DT5W-id. Blank = DT5W-id only.">
              <input
                name="leadCodePrefix"
                value={form.leadCodePrefix}
                onChange={changeHandler}
                placeholder="e.g. ACME"
                maxLength={32}
                style={saInputSx}
              />
            </SaField>
            <SaField label="Agency logo" hint="PNG or JPG, optional.">
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                style={{ fontSize: "13px", color: SA.muted }}
              />
            </SaField>
          </div>

          <div
            style={{
              marginTop: "28px",
              paddingTop: "22px",
              borderTop: `1px solid ${SA.border}`,
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-check"}>
              {saving ? "Saving…" : "Create client"}
            </SaPrimaryButton>
            <SaGhostButton type="button" onClick={() => navigate("/client")}>
              Cancel
            </SaGhostButton>
          </div>
        </SaCard>
      </form>
    </SaPageShell>
  );
}

export default AddClient;
